import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';

export type FileRequest = <T>(path: string, init?: RequestInit) => Promise<T>;
interface Session { id: string; file_id: string; filename: string; size: number; fingerprint: string; part_bytes: number; expires_at: number;
  parts: Array<{ part_number: number; etag: string; size: number }> }
const english = { choose: 'Choose files', upload: 'Upload files', pause: 'Pause', resume: 'Reselect a paused file to resume it.',
  hint: 'Videos up to 10 GB; documents up to 1 GB. Interrupted uploads can resume for 48 hours.',
  complete: 'Uploaded. Preparing your files…', error: 'Upload paused. Check your connection and reselect the same file to resume.', unsupported: 'Choose a supported document or video.',
  tooLarge: 'This file exceeds the upload limit.', uploading: 'Uploading', scanning: 'Preparing files', cancel: 'Cancel upload' };
export function formatFileBytes(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${Math.ceil(bytes / 1000)} KB`;
}
export async function fileFingerprint(file: File): Promise<string> {
  const metadata = new TextEncoder().encode(`${file.name}\n${file.size}\n${file.lastModified}\n`);
  const start = new Uint8Array(await file.slice(0, 65536).arrayBuffer()), end = new Uint8Array(await file.slice(Math.max(0, file.size - 65536)).arrayBuffer());
  const input = new Uint8Array(metadata.length + start.length + end.length); input.set(metadata); input.set(start, metadata.length); input.set(end, metadata.length + start.length);
  return [...new Uint8Array(await crypto.subtle.digest('SHA-256', input))].map(n => n.toString(16).padStart(2, '0')).join('');
}
function uploadPart(url: string, blob: Blob, signal: AbortSignal, progress: (bytes: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    signal.addEventListener('abort', abort, { once: true });
    xhr.open('PUT', url); xhr.timeout = 180_000;
    xhr.upload.onprogress = e => progress(e.loaded);
    xhr.onload = () => { const etag = xhr.getResponseHeader('ETag'); xhr.status >= 200 && xhr.status < 300 && etag ? resolve(etag) : reject(new Error('upload_part_failed')); };
    xhr.onerror = xhr.ontimeout = () => reject(new Error('upload_part_failed'));
    xhr.onabort = () => reject(new DOMException('Paused', 'AbortError'));
    xhr.onloadend = () => signal.removeEventListener('abort', abort);
    if (signal.aborted) { reject(new DOMException('Paused', 'AbortError')); return; }
    xhr.send(blob);
  });
}
export function FileUploader({ request, path, folderId, onComplete, disabled = false, labels = {}, replacesId }: {
  request: FileRequest; path: string; folderId: string; onComplete: () => void; disabled?: boolean;
  labels?: Partial<typeof english>; replacesId?: string;
}) {
  const words = { ...english, ...labels }, [files, setFiles] = useState<File[]>([]), [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ name: '', bytes: 0, total: 0 }), [message, setMessage] = useState('');
  const controller = useRef<AbortController | null>(null), activeUpload = useRef<string | null>(null);
  useEffect(() => () => controller.current?.abort(), []);
  async function upload() {
    if (!folderId || busy) return;
    setBusy(true); setMessage(''); const control = new AbortController(); controller.current = control;
    try {
      for (const file of files) {
        const video = /\.(mp4|mov|m4v|webm)$/i.test(file.name);
        if (!/\.(pdf|xlsx|xls|csv|txt|docx|pptx|zip|png|jpe?g|mp4|mov|m4v|webm)$/i.test(file.name)) throw new Error('unsupported');
        if (!file.size || file.size > (video ? 10e9 : 1e9)) throw new Error('too_large');
        const fingerprint = await fileFingerprint(file), storageKey = `fd-file-upload:${path}:${folderId}:${fingerprint}`;
        let session: Session | null = null;
        let saved: { id?: string; expires_at?: number } | null = null;
        try {
          saved = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
        } catch { /* Optional resume storage. */ }
        if (saved?.id && (saved.expires_at ?? 0) > Date.now()) {
          try { session = await request<Session>(`${path}/${saved.id}`, { signal: control.signal }); }
          catch (error) {
            // A transient network failure must not discard the confirmed parts and
            // reserve another large object. Only an expired/missing session restarts.
            if (![404, 410].includes(Number((error as { status?: number } | null)?.status))) throw error;
            try { localStorage.removeItem(storageKey); } catch { /* Optional resume storage. */ }
          }
        }
        if (!session) {
          session = await request<Session>(path, { method: 'POST', signal: control.signal, body: JSON.stringify({ folder_id: folderId, filename: file.name, size: file.size, fingerprint, replaces_id: replacesId ?? null }) });
          try { localStorage.setItem(storageKey, JSON.stringify({ id: session.id, expires_at: session.expires_at })); } catch { /* Storage-disabled browsers can still upload. */ }
        }
        if (session.fingerprint !== fingerprint || session.size !== file.size) throw new Error('resume_mismatch');
        const current = session; activeUpload.current = `${path}/${current.id}`;
        const done = new Map(current.parts.map(p => [p.part_number, p.size])), inflight = new Map<number, number>();
        const render = () => setProgress({ name: file.name, bytes: [...done.values(), ...inflight.values()].reduce((n, x) => n + x, 0), total: file.size });
        render();
        const pending = Array.from({ length: Math.ceil(file.size / current.part_bytes) }, (_, i) => i + 1).filter(part => !done.has(part));
        const worker = async () => {
          while (pending.length) {
            const part = pending.shift()!;
            if (control.signal.aborted) throw new DOMException('Paused', 'AbortError');
            const blob = file.slice((part - 1) * current.part_bytes, Math.min(file.size, part * current.part_bytes));
            let uploaded = false;
            for (let attempt = 0; attempt < 4 && !uploaded; attempt++) {
              try {
                const { url } = await request<{ url: string }>(`${path}/${current.id}/parts/${part}/url`, { method: 'POST', signal: control.signal });
                const etag = await uploadPart(url, blob, control.signal, bytes => { inflight.set(part, bytes); render(); });
                await request(`${path}/${current.id}/parts/${part}`, { method: 'POST', body: JSON.stringify({ etag }), signal: control.signal });
                inflight.delete(part); done.set(part, blob.size); render(); uploaded = true;
              } catch (error) {
                inflight.delete(part); render(); if (control.signal.aborted || attempt === 3) throw error;
                await new Promise(resolve => setTimeout(resolve, 1000 * 2 ** attempt));
              }
            }
          }
        };
        await Promise.all([worker(), worker()]);
        await request(`${path}/${current.id}/complete`, { method: 'POST', signal: control.signal });
        try { localStorage.removeItem(storageKey); } catch { /* Optional resume storage. */ }
        activeUpload.current = null; onComplete();
      }
      setFiles([]); setMessage(words.complete);
    } catch (error) {
      control.abort();
      const code = error instanceof Error ? error.message : '';
      setMessage(code === 'unsupported' ? words.unsupported : code === 'too_large' ? words.tooLarge : words.error);
    } finally { setBusy(false); controller.current = null; }
  }
  async function cancel() {
    controller.current?.abort();
    if (activeUpload.current) {
      try { await request(activeUpload.current, { method: 'DELETE' }); setFiles([]); setMessage(''); }
      catch { setMessage(words.error); }
      activeUpload.current = null;
    }
  }
  return <div className="fd-file-uploader">
    <label className="fd-file-drop"><span>{words.choose}</span><input aria-label={words.choose} type="file" multiple={!replacesId} disabled={disabled || busy} accept=".pdf,.xlsx,.xls,.csv,.txt,.docx,.pptx,.zip,.png,.jpg,.jpeg,.mp4,.mov,.m4v,.webm" onChange={e => { setFiles(Array.from(e.target.files ?? [])); setMessage(''); e.target.value = ''; }} /><small>{words.hint}</small></label>
    {!!files.length && <ul className="fd-file-queue">{files.map((file, i) => <li key={`${file.name}:${i}`}><span>{file.name}</span><small>{formatFileBytes(file.size)}</small></li>)}</ul>}
    {busy && <div role="status" aria-live="polite"><p>{words.uploading} {progress.name} · {formatFileBytes(progress.bytes)} / {formatFileBytes(progress.total)}</p><progress aria-label={words.uploading} max={progress.total || 1} value={progress.bytes} /></div>}
    <div className="fd-file-actions"><Button variant="secondary" disabled={disabled || !folderId || !files.length || busy} onClick={() => void upload()}>{words.upload}</Button>
      {busy && <Button variant="ghost" onClick={() => controller.current?.abort()}>{words.pause}</Button>}
      {!busy && activeUpload.current && <Button variant="ghost" onClick={() => void cancel()}>{words.cancel}</Button>}</div>
    {message && <p role="status" className="fd-body-sm">{message}</p>}
  </div>;
}
